// Public intake form schema — validated server-side with Zod (PRD Module A).

import { z } from 'zod';
import { PACKAGE_IDS } from './packages';

export const ROLE_LEVELS = ['exec', 'director', 'manager', 'ic', 'other'] as const;
export const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-1000', '1000_plus'] as const;
export const TIMELINES = ['asap', 'this_quarter', 'this_year', 'exploring'] as const;
export const BUDGET_RANGES = ['under_5k', '5k_15k', '15k_50k', '50k_plus', 'unsure'] as const;
export const REGULATORY = ['none', 'some', 'regulated'] as const;

export const INDUSTRIES = [
  'Software & Technology',
  'Financial Services',
  'Healthcare & Life Sciences',
  'Manufacturing',
  'Retail & E-commerce',
  'Professional Services',
  'Public Sector & Education',
  'Telecom & Media',
  'Energy & Utilities',
  'Construction & Real Estate',
  'Transportation & Logistics',
  'Other',
] as const;

export const intakeSchema = z.object({
  contact_name: z.string().trim().min(2).max(120),
  contact_email: z.string().trim().email().max(254),
  contact_role: z.string().trim().max(120).optional().default(''),
  role_level: z.enum(ROLE_LEVELS),
  company_name: z.string().trim().min(2).max(160),
  company_website: z.string().trim().max(200).optional().default(''),
  industry: z.enum(INDUSTRIES),
  company_size: z.enum(COMPANY_SIZES),
  primary_goal: z.string().trim().min(20, 'Tell us a bit more (20+ characters) so we can prepare.').max(2000),
  offer_interest: z
    .enum(['not_sure', ...PACKAGE_IDS] as unknown as [string, ...string[]])
    .default('not_sure'),
  ai_maturity_self_rating: z.coerce.number().int().min(1).max(5),
  current_ai_tools: z.string().trim().max(500).optional().default(''),
  data_classification: z.enum(['public', 'internal', 'confidential', 'restricted']).default('internal'),
  regulatory_exposure: z.enum(REGULATORY).default('none'),
  timeline: z.enum(TIMELINES),
  budget_range: z.enum(BUDGET_RANGES),
  compelling_event: z.string().trim().max(500).optional().default(''),
  consent: z.literal(true, {
    errorMap: () => ({ message: 'Consent is required so we can contact you about this inquiry.' }),
  }),
  // Spam controls. The honeypot intentionally VALIDATES when filled — the route
  // detects it and returns a fake success so bots learn nothing.
  website: z.string().max(200).optional().default(''),
  started_at: z.coerce.number().int().positive(),
  // Attribution
  utm: z
    .object({
      source: z.string().max(120).optional(),
      medium: z.string().max(120).optional(),
      campaign: z.string().max(120).optional(),
      term: z.string().max(120).optional(),
      content: z.string().max(120).optional(),
    })
    .partial()
    .optional(),
  referrer: z.string().max(500).optional().default(''),
});

export type IntakePayload = z.infer<typeof intakeSchema>;

/** Best-effort registrable domain from a website-ish string. */
export function deriveDomain(website: string | null | undefined): string | null {
  if (!website) return null;
  try {
    const u = new URL(website.includes('://') ? website : `https://${website}`);
    const host = u.hostname.replace(/^www\./i, '').toLowerCase();
    return host.includes('.') ? host : null;
  } catch {
    return null;
  }
}
